/**
 * Sandbox List Page
 *
 * Hosts the v1 (template-first) and v2 (application-first) sandbox views. A
 * v1 | v2 toggle shows when the user has both flags; the URL's `?view=` wins
 * over the saved default, and only "Set as default" saves it.
 *
 * @module features/sandboxes/pages/SandboxListPage
 */

import React from 'react';
import { Button, Flex, PillToggle, type PillToggleOption } from '@chatsdet/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth } from '../../../contexts/AuthContext';
import { useSandboxesViewPrefs } from '../../../hooks/useUserSettings';
import { SandboxV1View } from '../components/SandboxV1View';
import { SandboxV2View } from '../components/v2/SandboxV2View';
import {
  resolveSandboxesView,
  validateSandboxesListSearch,
  type SandboxesView,
} from '../sandboxesRouteSearch';

const VIEW_OPTIONS: readonly PillToggleOption<SandboxesView>[] = [
  { value: 'v1', label: 'v1' },
  { value: 'v2', label: 'v2' },
];

export const SandboxListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // The route's own validator already ran; strict:false avoids importing the router (a cycle through routes.ts).
  const search = validateSandboxesListSearch(useSearch({ strict: false }));
  const { defaultView, setDefaultView } = useSandboxesViewPrefs();

  const hasV1 = Boolean(user?.features?.environments);
  const hasV2 = Boolean(user?.features?.sandboxV2);
  const view = resolveSandboxesView({ requested: search.view, savedDefault: defaultView, hasV1, hasV2 });

  React.useEffect(() => {
    if (user && view === null) {
      void navigate({ to: '/' });
    }
  }, [user, view, navigate]);

  if (view === null) {
    return null;
  }

  const switcher =
    hasV1 && hasV2 ? (
      <Flex align="center" gap="sm">
        {view !== defaultView && (
          <Button variant="ghost" size="sm" onPress={() => setDefaultView(view)}>
            Set as default
          </Button>
        )}
        <PillToggle
          aria-label="Sandboxes version"
          value={view}
          onChange={(next) => void navigate({ to: '/sandboxes', search: { view: next } })}
          options={VIEW_OPTIONS}
        />
      </Flex>
    ) : null;

  return view === 'v2' ? <SandboxV2View switcher={switcher} /> : <SandboxV1View switcher={switcher} />;
};
